import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, KeyRound, Mail, Monitor, Users } from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, Label, PasswordInput } from "@/components/ui/Form.jsx";
import { Avatar, Callout, DefItem, EmptyState } from "@/components/ui/Display.jsx";
import Tag, { RoleTag } from "@/components/ui/Tag.jsx";
import { useToast } from "@/components/ui/feedback.js";
import { useTheme } from "@/context/theme.js";

import { authService, teamService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { formatDateTime, formatLastActive } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * Replaces frontend/app/(dashboard)/profile/page.js.
 *
 * TWO OF HTML-MARKUP'S CONTROLS HAVE NO API AND ARE NOT HERE:
 *
 *   Editable email        userController.updateUser accepts { name, role,
 *                         isActive, settings } and nothing else. The email is
 *                         also the sign-in username, so changing it is a real
 *                         operation — not one this form can quietly perform.
 *                         Shown read-only with the reason.
 *
 *   "Sign out everywhere" There is no session store to sign out OF. Auth is a
 *                         stateless JWT with no server-side registry (a
 *                         deliberate design decision in this project), so the
 *                         markup's three-session list and its "sign out
 *                         everywhere else" button had nothing behind them and
 *                         would have been a button that lies.
 *
 * What IS real, and is shown instead, is the tracked DEVICE: SystemInfo holds
 * the machine that registered the desktop app for this account.
 */

const passwordSchema = z
    .object({
        currentPassword: z.string().min(1, "Enter your current password"),
        // The server's own floor — see authController.changePassword.
        newPassword: z.string().min(8, "At least 8 characters"),
        confirmPassword: z.string().min(1, "Re-enter the new password"),
    })
    .refine((v) => v.newPassword === v.confirmPassword, {
        path: ["confirmPassword"],
        message: "The two passwords do not match",
    })
    .refine((v) => v.newPassword !== v.currentPassword, {
        path: ["newPassword"],
        message: "The new password must differ from the current one",
    });

const STRENGTH = [
    { label: "Too short", tone: "bg-slate-300 dark:bg-slate-600", text: "text-slate-400" },
    { label: "Weak", tone: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
    { label: "Fair", tone: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
    { label: "Good", tone: "bg-brand-500", text: "text-brand-600 dark:text-brand-400" },
    { label: "Strong", tone: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
];

/*
 * A COARSE hint, not a security measure. It counts character classes, which is
 * why "Passw0rd!" scores well and is still a bad password — the meter says how
 * varied the input is, and the label deliberately stops short of "safe".
 */
function scorePassword(pw) {
    if (!pw || pw.length < 8) return 0;
    let score = 1;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return Math.min(score, 4);
}

export default function Profile() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const { user: me, refresh } = useAuth();
    const { theme, setTheme } = useTheme();

    const userId = me?.id ?? me?._id;

    const userQuery = useQuery({
        queryKey: ["user", userId],
        enabled: Boolean(userId),
        queryFn: () => userService.getUser(userId).then((r) => r.data?.data ?? {}),
    });

    const teamsQuery = useQuery({
        queryKey: ["teams"],
        queryFn: () => teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
    });

    const user = userQuery.data?.user ?? me ?? null;
    const systemInfo = userQuery.data?.systemInfo ?? null;

    const teamName = useMemo(
        () =>
            teamsQuery.data?.find((t) =>
                (t.members ?? []).some((m) => (typeof m === "object" ? m._id : m) === userId),
            )?.name ?? null,
        [teamsQuery.data, userId],
    );

    /* ---- Name ---- */
    const [name, setName] = useState(() => me?.name ?? "");
    const nameDirty = user ? name.trim() !== (user.name ?? "") : false;

    const saveName = useMutation({
        mutationFn: () => userService.updateUser(userId, { name: name.trim() }),
        onSuccess: async () => {
            toast("Profile updated");
            queryClient.invalidateQueries({ queryKey: ["user", userId] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
            // The topbar and sidebar read the name from the session, so it has
            // to be re-read or the page and the chrome disagree.
            await refresh?.();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not save your profile."), "error"),
    });

    /* ---- Password ---- */
    const {
        register,
        handleSubmit,
        control,
        reset,
        setError,
        formState: { errors, isSubmitting },
    } = useForm({ resolver: zodResolver(passwordSchema), mode: "onBlur" });

    // useWatch, not watch(): the React Compiler cannot memoize a component that
    // calls watch(), and silently skips optimising the whole thing.
    const newPassword = useWatch({ control, name: "newPassword" }) ?? "";
    const confirmPassword = useWatch({ control, name: "confirmPassword" }) ?? "";
    const score = scorePassword(newPassword);
    const strength = STRENGTH[score];
    const matches = confirmPassword.length > 0 && confirmPassword === newPassword;

    const changePassword = async (values) => {
        try {
            await authService.changePassword({
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            });
            toast("Password changed");
            reset();
        } catch (error) {
            // A wrong current password is a field error, not a page error — it
            // belongs next to the input the person has to correct.
            const message = apiErrorMessage(error, "Could not change your password.");
            if (error?.response?.status === 401 || /current password/i.test(message)) {
                setError("currentPassword", { message: "That is not your current password" });
            } else {
                setError("root", { message });
            }
        }
    };

    if (!user) {
        return (
            <>
                <PageChrome breadcrumb={["Profile"]} title="My Profile" />
                <main className="mx-auto w-full max-w-[1100px] p-4 lg:p-5">
                    <h1 className="text-[22px] font-semibold tracking-tight">My Profile</h1>
                    <p className="mt-4 text-[13px] text-slate-400 dark:text-slate-500">Loading…</p>
                </main>
            </>
        );
    }

    return (
        <>
            <PageChrome breadcrumb={["Profile"]} title="My Profile" />

            <main className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 p-4 lg:p-5">
                {/* ---- Identity ---- */}
                <Card>
                    <div className="flex flex-wrap items-center gap-5 p-4.5">
                        <Avatar name={user.name} size="2xl" />
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2.5">
                                <h1 className="text-xl font-semibold tracking-tight">{user.name}</h1>
                                <RoleTag role={user.role} />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-slate-500 dark:text-slate-400">
                                <span className="flex items-center gap-1.5">
                                    <Mail className="size-4 shrink-0" strokeWidth={1.8} />
                                    {user.email}
                                </span>
                                {teamName && (
                                    <span className="flex items-center gap-1.5">
                                        <Users className="size-4 shrink-0" strokeWidth={1.8} />
                                        {teamName}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
                    <div className="flex min-w-0 flex-col gap-5">
                        {/* ---- Profile details ---- */}
                        <Card>
                            <CardHead>
                                <CardTitle>Profile details</CardTitle>
                                {nameDirty && (
                                    <CardExtra>
                                        <Tag tone="amber" data-profile-dirty>
                                            Unsaved
                                        </Tag>
                                    </CardExtra>
                                )}
                            </CardHead>
                            <CardBody className="space-y-4">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="pfName">Full name</Label>
                                        <Input
                                            id="pfName"
                                            data-field="name"
                                            size="lg"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="pfEmail">Email</Label>
                                        <Input
                                            id="pfEmail"
                                            size="lg"
                                            value={user.email}
                                            disabled
                                            readOnly
                                        />
                                        {/* Not an oversight: updateUser does not
                                            accept an email, and this address is
                                            the sign-in username. */}
                                        <p className="hint" data-email-note>
                                            Your email is your sign-in username and cannot be
                                            changed here — ask an administrator.
                                        </p>
                                    </div>
                                    <div>
                                        <Label htmlFor="pfRole">Role</Label>
                                        <Input id="pfRole" size="lg" value={user.role} disabled readOnly />
                                    </div>
                                    <div>
                                        <Label htmlFor="pfTeam">Team</Label>
                                        <Input
                                            id="pfTeam"
                                            size="lg"
                                            value={teamName ?? "No team"}
                                            disabled
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2.5">
                                    <Button
                                        onClick={() => setName(user.name ?? "")}
                                        disabled={!nameDirty || saveName.isPending}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="ml-auto"
                                        disabled={!nameDirty || !name.trim()}
                                        loading={saveName.isPending}
                                        onClick={() => saveName.mutate()}
                                        data-save-profile
                                    >
                                        Save changes
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>

                        {/* ---- Password ---- */}
                        <Card>
                            <CardHead>
                                <CardTitle
                                    icon={<KeyRound className="size-4 text-slate-400" strokeWidth={2} />}
                                >
                                    Change password
                                </CardTitle>
                            </CardHead>
                            <form
                                noValidate
                                onSubmit={handleSubmit(changePassword)}
                                className="card-body space-y-4"
                            >
                                {errors.root && (
                                    <Callout tone="danger" data-password-error>
                                        {errors.root.message}
                                    </Callout>
                                )}

                                <Field label="Current password" error={errors.currentPassword?.message} htmlFor="pwCurrent">
                                    <PasswordInput
                                        id="pwCurrent"
                                        size="lg"
                                        autoComplete="current-password"
                                        placeholder="Enter your current password"
                                        {...register("currentPassword")}
                                    />
                                </Field>

                                <Field
                                    label="New password"
                                    error={errors.newPassword?.message}
                                    htmlFor="pwNew"
                                >
                                    <PasswordInput
                                        id="pwNew"
                                        size="lg"
                                        autoComplete="new-password"
                                        placeholder="At least 8 characters"
                                        {...register("newPassword")}
                                    />
                                    {newPassword.length > 0 && (
                                        <div className="mt-2" data-strength={strength.label}>
                                            <div className="flex gap-1">
                                                {[0, 1, 2, 3].map((i) => (
                                                    <span
                                                        key={i}
                                                        className={cx(
                                                            "h-1 flex-1 rounded-full transition",
                                                            i < score
                                                                ? strength.tone
                                                                : "bg-slate-200 dark:bg-slate-700",
                                                        )}
                                                    />
                                                ))}
                                            </div>
                                            <p className={cx("mt-1 text-[11.5px]", strength.text)}>
                                                {strength.label}
                                                <span className="text-slate-400 dark:text-slate-500">
                                                    {" "}
                                                    — a rough guide to variety, not a guarantee.
                                                </span>
                                            </p>
                                        </div>
                                    )}
                                </Field>

                                <Field
                                    label="Confirm new password"
                                    error={errors.confirmPassword?.message}
                                    htmlFor="pwConfirm"
                                >
                                    <div className="relative">
                                        <PasswordInput
                                            id="pwConfirm"
                                            size="lg"
                                            autoComplete="new-password"
                                            placeholder="Re-enter the new password"
                                            {...register("confirmPassword")}
                                        />
                                        {matches && (
                                            <span
                                                data-password-match
                                                className="pointer-events-none absolute top-1/2 right-11 -translate-y-1/2 text-emerald-500"
                                            >
                                                <Check className="size-4.5" strokeWidth={2.4} />
                                            </span>
                                        )}
                                    </div>
                                </Field>

                                <div className="flex justify-end">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        loading={isSubmitting}
                                        loadingLabel="Changing…"
                                        data-change-password
                                    >
                                        Change password
                                    </Button>
                                </div>
                            </form>
                        </Card>
                    </div>

                    <div className="flex min-w-0 flex-col gap-5">
                        {/* ---- Account ---- */}
                        <Card>
                            <CardHead>
                                <CardTitle>Account</CardTitle>
                            </CardHead>
                            <CardBody className="space-y-3.5">
                                <DefItem label="Organization">
                                    {me?.organizationName ?? "—"}
                                </DefItem>
                                <DefItem label="Member since">
                                    {user.createdAt ? formatDateTime(user.createdAt) : "—"}
                                </DefItem>
                                <DefItem label="Last active">
                                    {formatLastActive(user.lastActive ?? user.updatedAt)}
                                </DefItem>
                                <DefItem label="User ID">
                                    <span className="font-mono text-[12px] text-slate-400">
                                        {userId}
                                    </span>
                                </DefItem>
                            </CardBody>
                        </Card>

                        {/* ---- Appearance ---- */}
                        <Card>
                            <CardHead>
                                <CardTitle>Appearance</CardTitle>
                            </CardHead>
                            <CardBody>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        ["light", "Light"],
                                        ["dark", "Dark"],
                                    ].map(([key, label]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            data-theme-pick={key}
                                            aria-pressed={theme === key}
                                            onClick={() => setTheme(key)}
                                            className={cx(
                                                "rounded-lg border px-3 py-2.5 text-[13px] font-medium transition",
                                                theme === key
                                                    ? "border-brand-500 bg-brand-50/60 text-brand-700 dark:border-brand-600 dark:bg-brand-950/40 dark:text-brand-200"
                                                    : "border-slate-200 text-slate-600 hover:border-slate-300 dark:border-slate-800 dark:text-slate-300",
                                            )}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <p className="hint mt-2">
                                    Stored in this browser only — it does not follow you to another
                                    device.
                                </p>
                            </CardBody>
                        </Card>

                        {/* ---- Tracked device ---- */}
                        <Card>
                            <CardHead>
                                <CardTitle
                                    icon={<Monitor className="size-4 text-slate-400" strokeWidth={2} />}
                                >
                                    Tracked device
                                </CardTitle>
                            </CardHead>
                            {systemInfo ? (
                                <CardBody className="space-y-3.5" data-device>
                                    <DefItem label="Device">{systemInfo.deviceName}</DefItem>
                                    <DefItem label="Operating system">
                                        {[systemInfo.os, systemInfo.osVersion]
                                            .filter(Boolean)
                                            .join(" ")}
                                    </DefItem>
                                    <DefItem label="App version">
                                        <span className="font-mono">{systemInfo.appVersion}</span>
                                    </DefItem>
                                    <DefItem label="Timezone">{systemInfo.timezone}</DefItem>
                                    {/*
                                     * Not "sessions". Sign-in is a stateless
                                     * JWT with no server-side registry, so
                                     * there is no list of live sessions to show
                                     * and nothing to revoke — the markup's
                                     * "sign out everywhere else" button had
                                     * nothing to call.
                                     */}
                                    <p className="hint">
                                        This is the machine your desktop tracker is registered on.
                                        Sign-in itself is not tracked per device, so there is no
                                        session list to revoke.
                                    </p>
                                </CardBody>
                            ) : (
                                <CardBody>
                                    <EmptyState
                                        title="No device registered"
                                        hint="This appears once you sign in to the desktop tracker."
                                    />
                                </CardBody>
                            )}
                        </Card>
                    </div>
                </div>
            </main>
        </>
    );
}
